<script lang="ts" setup>
import { computed, VNode } from 'vue';

import { InputIconPosition, InputVariant } from '@/types';

const modelValue = defineModel<string | number>({ default: "" });

interface Props {
  label?: string;
  type?: string;
  variant?: InputVariant;
  inputClass?: string | string[];
  icon?: string | object | (() => VNode) | null;
  labelIcon?: string | object | (() => VNode) | null;
  prefix?: string | null;
  suffix?: string | null;
  position?: InputIconPosition;
  testId?: string;
  required?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "",
  max: "",
  min: "",
  maxlength :"",
  type: "text",
  variant: "primary",
  inputClass: "",
  icon: null,
  labelIcon: null,
  prefix: null,
  suffix: null,
  position: "start",
  testId: "input",
  disabled: false,
  required: false
})

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "startIconClick"): void;
  (e: "endIconClick"): void;
}>();

const variantClass = computed(() => props.variant ?? 'primary');

const inputStyles = computed(() => {
  const styles = {
    paddingLeft: '',
    paddingRight: '',
    background: 'none',
  };

  if (props.icon && props.position === 'start') {
    styles.paddingLeft = '2.25rem';
  } else if (props.prefix) {
    styles.paddingLeft = '3.125rem';
  }
  
  if (props.icon && props.position === 'end') {
    styles.paddingRight = '2.25rem';
  } else if (props.suffix) {
    styles.paddingRight = '3.125rem';
  }

  if (modelValue.value && ['primary', 'admin'].includes(props.variant ?? '')) {
    styles.background = 'rgba(28, 86, 145, 0.05)';
  }

  return styles;
});
</script>

<template>
  <label :class="['w-100', variantClass]">
    <div v-if="label" class="input-label d-flex align-items-center">
      {{ label }}
      <span v-if="required">&ast;</span>
      <component v-if="labelIcon" :is="labelIcon" class="ms-1" />
    </div>

    <div class="input-wrapper position-relative">
      <span
        v-if="prefix"
        class="fix prefix-container" 
      >
        {{ prefix }}
      </span>

      <span
        v-if="icon && position === 'start'"
        class="component-wrapper component-start"
        @click="emit('startIconClick')"
      >
        <component :is="icon" />
      </span>

      <input
        :type="type"
        :class="inputClass"
        :style="inputStyles"
        :value="modelValue"
        :data-testid="testId"
        @input="e => emit('update:modelValue', (e.target as HTMLInputElement).value)"
        autocomplete="on"
        v-bind="$attrs"
      />

      <span
        v-if="icon && position === 'end'"
        class="component-wrapper component-end"
        @click="emit('endIconClick')"
      >
        <component :is="icon" />
      </span>

      <span
        v-if="suffix"
        class="fix suffix-container"
      >
        {{ suffix }}
      </span>
    </div>
  </label>
</template>

<style scoped>
label .input-label {
  margin-bottom: 0.25rem;
  font-size: var(--font-xs);
  font-weight: 400;
  text-transform: capitalize;
}

label .input-wrapper {
  width: 100%;
  height: 2.5rem;
  max-height: 2.5rem;
}

label input {
  padding: 0.25rem 0.5rem;
  width: 100%;
  height: 100%;
  font-size: var(--font-sm);
  font-weight: 400;
}

label input:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, currentColor 10%, transparent);
  outline: none;
}

label input:disabled {
  opacity: 0.75;
}

label input::placeholder {
  color: rgba(28, 86, 145, 0.2);
  font-size: var(--font-xs);
}

.fix {
  max-width: 3rem;
  background: #e1e8f0;
  font-size: 0.8rem;
  padding: 0 0.5rem;
  border: 1px solid var(--ch-input);
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1px 0 1px 1px;
}

.prefix-container {
  left: 0;
  border-radius: 5px 0 0 5px;
}

.suffix-container {
  right: 0;
  border-radius: 0 5px 5px 0;
}

.component-wrapper {
  width: 1.25rem;
  height: 1.25rem;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  -webkit-transform: translateY(-50%);
  -ms-transform: translateY(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
}

.component-start {
  left: 0;
  margin-left: 0.75rem;
}

.component-end {
  right: 0;
  margin-right: 0.75rem;
}

@media screen and (min-width: 1024px) {
  label .input-label {
    font-size: var(--font-sm);
  }
}

/* Variant Styles */

/* Primary Variant */
label.primary .input-label {
  color: var(--ch-black);
}

label.primary input {
  color: var(--ch-black);
  border: 1px solid var(--ch-blue-100);
  border-radius: 6px;
}

label.primary input:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 15%, transparent);
}

label.primary .fix {
  color: var(--ch-blue-100);
}

/* AdminV1 Variant */
label.admin .input-label,
label.admin .fix {
  color: var(--ch-text-head);
}

label.admin input {
  color: var(--ch-text-head);
  border: 1px solid var(--ch-input);
  border-radius: 5px;
}

/* Grey Variant */
label.grey .input-label {
  color: var(--ch-black);
}

label.grey input {
  color: var(--ch-black);
  border: 1px solid var(--ch-border-grey-30);
  border-radius: 6px;
}

label.grey input::placeholder {
  color: var(--ch-border-grey);
}

label.grey input:focus {
  border: 2px solid var(--ch-blue-100);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 10%, transparent);
}

/* Naked Variant */
label.naked .input-label,
label.naked .fix {
  color: currentColor;
}

label.naked input {
  color: currentColor;
  border: none;
  background: transparent;
}

label.naked input:focus {
  border-radius: 5px;
}

label.naked input::placeholder {
  color: rgba(28, 86, 145, 0.2);
  font-size: var(--font-sm);
}

/* ReadOnly Variant */
label input[readonly]::placeholder {
  color: var(--ch-black) !important; 
  font-size: var(--font-sm) !important;
}

label input[readonly] { 
  border: 1px solid var(--ch-border-grey-30) !important; 
  border-radius: 6px !important;
  color: var(--ch-black) !important;
  background-color: var(--ch-readonly) !important;
}

label input[readonly]:focus {
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-readonly) 15%, transparent) !important;
    outline: none !important;
    border: 1px solid var(--ch-border-grey-30) !important;
}
</style>
